'use client';

import React from 'react';
import { Box, Chip } from '@mui/material';
import SatelliteAltIcon from '@mui/icons-material/SatelliteAlt';
import PaidIcon from '@mui/icons-material/Paid';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import PublishIcon from '@mui/icons-material/Publish';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import FilterListIcon from '@mui/icons-material/FilterList';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { useAgentChat } from '../../hooks/useAgentChat';
import { usePageContext } from '../../hooks/usePageContext';

interface QuickAction {
  label: string;
  prompt: string | ((ctx: { orderId?: string; projectId?: string }) => string);
  icon: React.ReactElement;
}

const iconSx = { fontSize: '0.875rem' };

const DEFAULT_ACTIONS: QuickAction[] = [
  {
    label: 'Search imagery',
    prompt: 'What satellite imagery is available over San Francisco from the past month?',
    icon: <SatelliteAltIcon sx={iconSx} />,
  },
  {
    label: 'Get a quote',
    prompt: 'How much would high-resolution imagery of New York City cost?',
    icon: <PaidIcon sx={iconSx} />,
  },
  {
    label: 'Explore products',
    prompt: 'What satellite imagery products and providers are available on SkyWatch?',
    icon: <InfoOutlinedIcon sx={iconSx} />,
  },
  {
    label: 'My projects',
    prompt: "What's the status of my active projects?",
    icon: <FolderOpenIcon sx={iconSx} />,
  },
  {
    label: 'Publish to ESRI',
    prompt: 'I want to publish my delivered data to ArcGIS.',
    icon: <PublishIcon sx={iconSx} />,
  },
];

const PAGE_ACTIONS: Record<string, QuickAction[]> = {
  order_detail: [
    {
      label: 'Order status',
      prompt: ctx =>
        ctx.orderId
          ? `What is the status of order ${ctx.orderId}?`
          : 'What is the status of this order?',
      icon: <AssignmentIcon sx={iconSx} />,
    },
    {
      label: 'Publish to ESRI',
      prompt: ctx =>
        ctx.orderId ? `Publish order ${ctx.orderId} to ArcGIS` : 'Publish this order to ArcGIS',
      icon: <PublishIcon sx={iconSx} />,
    },
    {
      label: 'Get a quote',
      prompt: 'How much would similar imagery cost?',
      icon: <PaidIcon sx={iconSx} />,
    },
  ],
  orders: [
    {
      label: 'Recent orders',
      prompt: 'Show me my recent orders and their status',
      icon: <AssignmentIcon sx={iconSx} />,
    },
    {
      label: 'Publish to ESRI',
      prompt: 'Which of my orders can I publish to ArcGIS?',
      icon: <PublishIcon sx={iconSx} />,
    },
    {
      label: 'Delivery status',
      prompt: 'Check the delivery status of my recent orders',
      icon: <LocalShippingIcon sx={iconSx} />,
    },
  ],
  archive_search: [
    {
      label: 'Get a quote',
      prompt: 'How much would this imagery cost?',
      icon: <PaidIcon sx={iconSx} />,
    },
    {
      label: 'Explore products',
      prompt: 'What satellite imagery products and providers are available on SkyWatch?',
      icon: <InfoOutlinedIcon sx={iconSx} />,
    },
    {
      label: 'Compare providers',
      prompt: 'Compare the available satellite imagery providers for this area',
      icon: <CompareArrowsIcon sx={iconSx} />,
    },
  ],
  tasking: [
    {
      label: 'Tasking pricing',
      prompt: 'What would it cost to task a new satellite collection here?',
      icon: <PaidIcon sx={iconSx} />,
    },
    {
      label: 'Explore products',
      prompt: 'What satellites are available for tasking?',
      icon: <InfoOutlinedIcon sx={iconSx} />,
    },
    {
      label: 'Search archive',
      prompt: 'Is there any existing archive imagery for this area?',
      icon: <SatelliteAltIcon sx={iconSx} />,
    },
  ],
  projects: [
    {
      label: 'Project status',
      prompt: "What's the status of my active projects?",
      icon: <FolderOpenIcon sx={iconSx} />,
    },
    {
      label: 'Explore products',
      prompt: 'What satellite imagery products and providers are available on SkyWatch?',
      icon: <InfoOutlinedIcon sx={iconSx} />,
    },
    {
      label: 'Publish to ESRI',
      prompt: 'I want to publish my delivered data to ArcGIS.',
      icon: <PublishIcon sx={iconSx} />,
    },
  ],
  project_detail: [
    {
      label: 'Project details',
      prompt: ctx =>
        ctx.projectId ? `Tell me about project ${ctx.projectId}` : 'Tell me about this project',
      icon: <FolderOpenIcon sx={iconSx} />,
    },
    {
      label: 'Project orders',
      prompt: ctx =>
        ctx.projectId
          ? `What orders are in project ${ctx.projectId}?`
          : 'What orders are in this project?',
      icon: <AssignmentIcon sx={iconSx} />,
    },
    {
      label: 'Search imagery',
      prompt: 'Search for more satellite imagery for this project',
      icon: <SatelliteAltIcon sx={iconSx} />,
    },
  ],
  searches: [
    {
      label: 'Search imagery',
      prompt: 'What satellite imagery is available over San Francisco from the past month?',
      icon: <SatelliteAltIcon sx={iconSx} />,
    },
    {
      label: 'Get a quote',
      prompt: 'How much would high-resolution imagery cost?',
      icon: <PaidIcon sx={iconSx} />,
    },
    {
      label: 'Explore products',
      prompt: 'What satellite imagery products and providers are available on SkyWatch?',
      icon: <InfoOutlinedIcon sx={iconSx} />,
    },
  ],
  data_management: [
    {
      label: 'Order status',
      prompt: 'Show me my recent orders and their status',
      icon: <AssignmentIcon sx={iconSx} />,
    },
    {
      label: 'Publish to ESRI',
      prompt: 'Which of my delivered orders can I publish to ArcGIS?',
      icon: <PublishIcon sx={iconSx} />,
    },
    {
      label: 'Filter orders',
      prompt: 'Show me orders from the last month',
      icon: <FilterListIcon sx={iconSx} />,
    },
  ],
  order_imagery: [
    {
      label: 'Search imagery',
      prompt: 'What satellite imagery is available over San Francisco from the past month?',
      icon: <SatelliteAltIcon sx={iconSx} />,
    },
    {
      label: 'Get a quote',
      prompt: 'How much would high-resolution imagery of New York City cost?',
      icon: <PaidIcon sx={iconSx} />,
    },
    {
      label: 'Explore products',
      prompt: 'What satellite imagery products and providers are available on SkyWatch?',
      icon: <InfoOutlinedIcon sx={iconSx} />,
    },
  ],
  approvals: [
    {
      label: 'Recent orders',
      prompt: 'Show me my recent orders and their status',
      icon: <AssignmentIcon sx={iconSx} />,
    },
    {
      label: 'Account info',
      prompt: 'Show me my account details and permissions',
      icon: <AccountCircleIcon sx={iconSx} />,
    },
    {
      label: 'Billing info',
      prompt: 'Show me my billing information and usage',
      icon: <ReceiptLongIcon sx={iconSx} />,
    },
  ],
  metrics: [
    {
      label: 'Project status',
      prompt: "What's the status of my active projects?",
      icon: <FolderOpenIcon sx={iconSx} />,
    },
    {
      label: 'Recent orders',
      prompt: 'Show me my recent orders and their status',
      icon: <AssignmentIcon sx={iconSx} />,
    },
    {
      label: 'Billing info',
      prompt: 'Show me my billing information and usage',
      icon: <ReceiptLongIcon sx={iconSx} />,
    },
  ],
  organization: [
    {
      label: 'Account info',
      prompt: 'Show me my account details and role',
      icon: <AccountCircleIcon sx={iconSx} />,
    },
    {
      label: 'Team members',
      prompt: 'Who are the members of my organization?',
      icon: <AccountCircleIcon sx={iconSx} />,
    },
    {
      label: 'Billing info',
      prompt: 'Show me my billing information and usage',
      icon: <ReceiptLongIcon sx={iconSx} />,
    },
  ],
  account: [
    {
      label: 'Account info',
      prompt: 'Show me my account details and permissions',
      icon: <AccountCircleIcon sx={iconSx} />,
    },
    {
      label: 'Billing info',
      prompt: 'Show me my billing information and usage',
      icon: <ReceiptLongIcon sx={iconSx} />,
    },
    {
      label: 'Recent orders',
      prompt: 'Show me my recent orders and their status',
      icon: <AssignmentIcon sx={iconSx} />,
    },
  ],
  product_library: [
    {
      label: 'Explore products',
      prompt: 'What satellite imagery products and providers are available on SkyWatch?',
      icon: <InfoOutlinedIcon sx={iconSx} />,
    },
    {
      label: 'Compare providers',
      prompt: 'Compare the available satellite imagery providers and their capabilities',
      icon: <CompareArrowsIcon sx={iconSx} />,
    },
    {
      label: 'Get a quote',
      prompt: 'How much would high-resolution imagery cost?',
      icon: <PaidIcon sx={iconSx} />,
    },
  ],
};

export default function QuickActionBar() {
  const { sendMessage } = useAgentChat();
  const pageContext = usePageContext();

  const actions = PAGE_ACTIONS[pageContext.page || ''] || DEFAULT_ACTIONS;

  const resolvePrompt = (action: QuickAction): string => {
    if (typeof action.prompt === 'function') {
      return action.prompt({ orderId: pageContext.orderId, projectId: pageContext.projectId });
    }
    return action.prompt;
  };

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', justifyContent: 'center' }}>
      {actions.map(action => (
        <Chip
          key={action.label}
          label={action.label}
          icon={action.icon}
          onClick={() => sendMessage(resolvePrompt(action))}
          variant="outlined"
          sx={{
            color: 'var(--sw-white-color)',
            borderColor: 'rgba(244, 246, 255, 0.15)',
            borderRadius: '20px',
            fontSize: '0.8rem',
            '& .MuiChip-icon': { color: 'var(--primary-color)' },
            '&:hover': {
              borderColor: 'var(--primary-color)',
              bgcolor: 'rgba(71, 225, 188, 0.08)',
            },
          }}
        />
      ))}
    </Box>
  );
}
